import { useContext, useEffect, useState } from "react";
import { Box, Button, Modal, ModalClose, ModalDialog, ModalOverflow, Stack, Typography } from "@mui/joy";
import { motion } from "framer-motion";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import { LayoutContext } from "../../context";
import { APIGetQuizzes } from "../../api_quiz";
import QuizCard from "./QuizCard";
import QuizRunner from "./QuizRunner";

const LS_KEY = "onboardingQuizzesDismissed_v1";

/** Мягкое предложение пройти онбординговые тесты после настройки профиля.
 * Показывается один раз (пока не пройдены все / не отклонено), не форсит. */
export default function OnboardingQuizzesModal() {
  const { token, profileStatus } = useContext(LayoutContext);
  const [open, setOpen] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [activeCode, setActiveCode] = useState(null);

  const eligible =
    !!token &&
    profileStatus?.has_profile &&
    profileStatus?.basic_complete &&
    profileStatus?.partner_preferences_complete;

  const fetchOnboarding = async () => {
    try {
      const data = await APIGetQuizzes(token, { onboarding: true });
      return (data.quizzes || []).filter((q) => !q.is_completed);
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    let alive = true;
    if (!eligible) return;
    if (localStorage.getItem(LS_KEY)) return;
    (async () => {
      const incomplete = await fetchOnboarding();
      if (!alive) return;
      if (incomplete.length > 0) {
        setQuizzes(incomplete);
        setOpen(true);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, token]);

  const dismiss = () => {
    localStorage.setItem(LS_KEY, "1");
    setOpen(false);
  };

  const handleCompleted = async () => {
    const incomplete = await fetchOnboarding();
    setQuizzes(incomplete);
    if (incomplete.length === 0) {
      dismiss(); // все онбординговые пройдены — больше не предлагаем
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={dismiss}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(6px)", backgroundColor: "rgba(20,10,40,0.4)" } },
        }}
      >
        <ModalOverflow>
          <ModalDialog
            layout="center"
            sx={{ width: { xs: "94%", sm: 560 }, maxWidth: 560, borderRadius: "xl", p: { xs: 2.5, sm: 3 } }}
          >
            <ModalClose onClick={dismiss} />
            <Stack alignItems="center" spacing={1} sx={{ mb: 2, textAlign: "center" }}>
              <Box
                component={motion.div}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--joy-palette-primary-400), var(--joy-palette-primary-600))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <AutoAwesomeRoundedIcon />
              </Box>
              <Typography level="h3">Узнайте себя лучше</Typography>
              <Typography level="body-sm" sx={{ color: "text.tertiary", maxWidth: 420 }}>
                Это не анкета на 50 вопросов, а короткие мини-тесты. Пройдите — и подбор станет точнее.
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {quizzes.map((q, i) => (
                <QuizCard key={q.id} quiz={q} index={i} onOpen={(quiz) => setActiveCode(quiz.code)} />
              ))}
            </Box>

            <Button variant="plain" color="neutral" onClick={dismiss} sx={{ mt: 2, alignSelf: "center" }}>
              Позже
            </Button>
          </ModalDialog>
        </ModalOverflow>
      </Modal>

      <QuizRunner
        open={!!activeCode}
        code={activeCode}
        token={token}
        onClose={() => setActiveCode(null)}
        onCompleted={handleCompleted}
      />
    </>
  );
}